import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// Firebase configuration
// TODO: Replace with actual Firebase project credentials
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "YOUR_API_KEY",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "YOUR_AUTH_DOMAIN",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "YOUR_PROJECT_ID",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "YOUR_STORAGE_BUCKET",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "YOUR_MESSAGING_SENDER_ID",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "YOUR_APP_ID"
};

// VAPID key for web push
const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || "YOUR_VAPID_KEY";

let app;
let messaging;

// Initialize Firebase only in browser environment
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  try {
    app = initializeApp(firebaseConfig);
    messaging = getMessaging(app);
  } catch (error) {
    console.error('Firebase initialization error:', error);
  }
}

/**
 * Request notification permission from user
 * @returns {Promise<boolean>} - True if permission granted
 */
export const requestNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('Notification permission granted');
      return true;
    } else if (permission === 'denied') {
      console.log('Notification permission denied');
      return false;
    } else {
      console.log('Notification permission dismissed');
      return false;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Wait for service worker to become active
 * @param {ServiceWorkerRegistration} registration 
 * @returns {Promise<ServiceWorkerRegistration>}
 */
const waitForServiceWorkerActive = (registration) => {
  return new Promise((resolve, reject) => {
    if (registration.active) {
      resolve(registration);
      return;
    }

    const serviceWorker = registration.installing || registration.waiting;
    if (!serviceWorker) {
      reject(new Error('No service worker found'));
      return;
    }

    serviceWorker.addEventListener('statechange', function onStateChange(e) {
      if (e.target.state === 'activated') {
        serviceWorker.removeEventListener('statechange', onStateChange);
        resolve(registration);
      } else if (e.target.state === 'redundant') {
        serviceWorker.removeEventListener('statechange', onStateChange);
        reject(new Error('Service worker became redundant'));
      }
    });

    // Timeout after 10 seconds
    setTimeout(() => {
      reject(new Error('Service worker activation timeout'));
    }, 10000);
  });
};

/**
 * Get FCM token for push notifications
 * @returns {Promise<string|null>} - FCM token or null
 */
export const getFCMToken = async () => {
  try {
    if (!messaging) {
      console.error('Firebase messaging not initialized');
      return null;
    }

    // Register service worker
    let registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('Service Worker registered:', registration);

    // Wait for service worker to become active
    if (!registration.active) {
      console.log('Waiting for service worker to activate...');
      registration = await waitForServiceWorkerActive(registration);
      console.log('Service Worker is now active');
    }

    // Get FCM token
    const currentToken = await getToken(messaging, {
      vapidKey: vapidKey,
      serviceWorkerRegistration: registration
    });

    if (currentToken) {
      console.log('FCM Token obtained:', currentToken);
      return currentToken;
    } else {
      console.log('No registration token available');
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

/**
 * Listen for foreground messages
 * @param {Function} callback - Function to call when message received
 */
export const onMessageListener = (callback) => {
  if (!messaging) {
    console.error('Firebase messaging not initialized');
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    console.log('Message received in foreground:', payload);
    
    // Show notification even in foreground
    if ('Notification' in window && Notification.permission === 'granted') {
      const notificationTitle = payload.notification?.title || 'Chinmudra Academy';
      const notificationOptions = {
        body: payload.notification?.body || 'You have a new notification',
        icon: payload.notification?.icon || '/logo192.png',
        badge: '/logo192.png',
        tag: payload.data?.notification_id || 'notification',
        data: payload.data
      };

      new Notification(notificationTitle, notificationOptions);
    }
    
    // Call callback with payload
    if (callback) {
      callback(payload);
    }
  });
};

/**
 * Check if notifications are supported
 * @returns {boolean}
 */
export const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

/**
 * Get current notification permission status
 * @returns {string} - 'granted', 'denied', or 'default'
 */
export const getNotificationPermission = () => {
  if ('Notification' in window) {
    return Notification.permission;
  }
  return 'denied';
};

const pushNotificationService = {
  requestNotificationPermission,
  getFCMToken,
  onMessageListener,
  isNotificationSupported,
  getNotificationPermission
};

export default pushNotificationService;
